import { createSlice } from "@reduxjs/toolkit";
import {
  GetAllRootFilesAction,
  MoveFileAction,
  GetLargeFileAction,
  RenameFilesAction,
  UploadFilesAction,
} from "store/middlewares/files";
import { CreateShareLinkFileFolderAction } from "store/middlewares/folders";
import { matchesDateAndLocalFilter } from "utils/filterUtils";

const sortFiles = (files = [], sortBy, sortOrder) => {
  if (!sortBy || !sortOrder) return files;

  return [...files].sort((a, b) => {
    let comparison = 0;

    if (sortBy === "file_size") {
      const sizeA = Number(a?.file_size || 0);
      const sizeB = Number(b?.file_size || 0);
      comparison = sizeA - sizeB;
    } else {
      const dateA = Number(a?.created_at || 0);
      const dateB = Number(b?.created_at || 0);
      comparison = dateA - dateB;
    }

    return sortOrder === "desc" ? -comparison : comparison;
  });
};

export const filesSlice = createSlice({
  name: "files",
  initialState: {
    files: null,
    filesLoading: false,
    largeFile: null,
    largeFilesLoading: false,
    moveFileLoading: false,
    sortOrder: {
      sortBy: "file_size",
      sortOrder: "desc",
    },
  },
  reducers: {
    renameFileInFiles: (state, action) => {
      const updatedFile = action.payload;

      // update in files
      if (state.files?.data) {
        const index = state.files.data.findIndex(
          (f) => f.id === updatedFile.id,
        );
        if (index !== -1) state.files.data[index] = updatedFile;
      }

      // update in largeFile
      if (state.largeFile?.data) {
        const index = state.largeFile.data.findIndex(
          (f) => f.id === updatedFile.id,
        );
        if (index !== -1) state.largeFile.data[index] = updatedFile;
      }

      const file = state.largeFile?.data?.find((f) => f?.id === updatedFile?.id);
      if (file) file.file_name = updatedFile?.file_name;
    },
    uploadFileInFiles: (state, action) => {
      const newFiles = action.payload?.uploaded || [];

      if (newFiles.length === 0) return;
      if (newFiles[0]?.folder_id !== null) return;

      const { sortBy, sortOrder } = state.sortOrder || {};

      // Update files (root)
      if (state.files?.data) {
        const validNewFiles = newFiles.filter((file) =>
          matchesDateAndLocalFilter(file, state.files?.activeFilter),
        );

        state.files = {
          ...state.files,
          data: sortFiles(
            [...validNewFiles, ...(state.files?.data || [])],
            sortBy,
            sortOrder,
          ),
        };
      }

      // Update largeFile
      if (state.largeFile?.data) {
        const validLargeFiles = newFiles.filter((file) =>
          matchesDateAndLocalFilter(file, state.largeFile?.activeFilter),
        );
        state.largeFile = {
          ...state.largeFile,
          data: sortFiles(
            [...validLargeFiles, ...(state.largeFile?.data || [])],
            sortBy,
            sortOrder,
          ),
        };
      }
    },
    updateSortOrder: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortOrder = { sortBy, sortOrder };
    },
    starUnstarFiles: (state, action) => {
      const { files = [] } = action.payload || {};

      const applyStarStatus = (list, items) => {
        if (!Array.isArray(list) || !items.length) return;

        items.forEach((item) => {
          const target = list.find((entry) => entry?.id === item?.id);
          if (target && item.is_starred !== undefined) {
            target.is_starred = item.is_starred;
          }
        });
      };

      if (state.files?.data) {
        applyStarStatus(state.files.data, files);
      }

      if (state.largeFile?.data) {
        applyStarStatus(state.largeFile.data, files);
      }
    },

    lockUnlockFiles: (state, action) => {
      const { files = [] } = action.payload || {};
      const fileIds = files.map((file) => file.id);

      if (state.files?.data) {
        state.files.data.forEach((file) => {
          if (fileIds.includes(file.id)) {
            file.is_secure = !file.is_secure;
          }
        });
      }

      if (state.largeFile?.data) {
        state.largeFile.data.forEach((file) => {
          if (fileIds.includes(file.id)) {
            file.is_secure = !file.is_secure;
          }
        });
      }
    },

    updateFilePermission: (state, action) => {
      const response = action.payload || {};

      const {
        file_id,
        user_id,
        permission,
        profileId,
      } = response;

      if (
        !permission ||
        !user_id ||
        !profileId ||
        user_id !== profileId
      ) {
        return;
      }

      const updatePermission = (items = []) => {
        items.forEach((file) => {
          if (file_id && file.id === file_id) {
            file.permission = permission;
          }
        });
      };

      updatePermission(state.files?.data);
      updatePermission(state.largeFile?.data);
    },

    trashFiles: (state, action) => {
      const trashedFiles = action.payload?.files || [];
      const trashedFileIds = trashedFiles.map((f) => f.id);

      if (state.files?.data) {
        state.files.data.forEach((file) => {
          if (trashedFileIds.includes(file.id)) {
            file.is_trashed = true;
          }
        });
      }

      if (state.largeFile?.data) {
        state.largeFile.data.forEach((file) => {
          if (trashedFileIds.includes(file.id)) {
            file.is_trashed = true;
          }
        });
      }
    },
    restoreFiles: (state, action) => {
      const restoredFiles = action.payload?.files || [];
      if (!restoredFiles.length) return;

      const restoredFileIds = restoredFiles.map((file) => file.id);

      if (state.files?.data) {
        state.files.data.forEach((file) => {
          if (restoredFileIds.includes(file.id)) {
            file.is_trashed = false;
          }
        });
      }

      if (state.largeFile?.data) {
        state.largeFile.data.forEach((file) => {
          if (restoredFileIds.includes(file.id)) {
            file.is_trashed = false;
          }
        });
      }
    },
    perminentDeleteFiles: (state, action) => {
      const deletedFiles = action.payload?.files || [];

      const deletedFileIds = deletedFiles.map((f) => f.id);

      state.files = {
        ...state.files,
        data: state.files?.data?.filter(
          (file) => !deletedFileIds.includes(file.id),
        ),
      };
      state.largeFile = {
        ...state.largeFile,
        data: state.largeFile?.data?.filter(
          (file) => !deletedFileIds.includes(file.id),
        ),
      };
    },
    trashEmptyFiles: (state) => {
      state.files = {
        ...state.files,
        data: state.files?.data?.filter((file) => !file.is_trashed),
      };
      state.largeFile = {
        ...state.largeFile,
        data: state.largeFile?.data?.filter((file) => !file.is_trashed),
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(GetAllRootFilesAction.pending, (state) => {
        state.filesLoading = state.files?.data?.length > 0 ? false : true;
      })
      .addCase(GetAllRootFilesAction.fulfilled, (state, action) => {
        // state.files = action.payload.response;
        state.filesLoading = false;
        const newData = action.payload?.response;
        console.log(action.payload);
        if (newData.page === 1) {
          state.files = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          if (action.payload?.isLoadMore) {
            state.files = {
              ...newData,
              activeFilter: action.meta.arg,
              data: [...(state.files?.data || []), ...(newData?.data || [])],
            };
          } else {
            state.files = {
              ...newData,
              activeFilter: action.meta.arg,
            };
          }
        }
      })
      .addCase(GetAllRootFilesAction.rejected, (state) => {
        state.filesLoading = false;
      })
      .addCase(UploadFilesAction.fulfilled, (state, action) => {
        const { oldFileId } = action.payload || {};
        if (oldFileId) {
          state.files = {
            ...state.files,
            data: state.files?.data?.filter(
              (file) => file.id !== oldFileId,
            ),
          };
          state.largeFile = {
            ...state.largeFile,
            data: state.largeFile?.data?.filter(
              (file) => file.id !== oldFileId,
            ),
          };
        }
      })
      // .addCase(UploadFilesAction.fulfilled, (state, action) => {
      //   const newFiles = action.payload.response?.uploaded || [];
      //   // const mergedFiles = [...newFiles, ...state.files.data];
      //   // mergedFiles.sort((a, b) =>
      //   //   a.file_name.localeCompare(b.file_name, undefined, {
      //   //     numeric: true,
      //   //     sensitivity: "base",
      //   //   }),
      //   // );
      //   if (action.payload.response?.uploaded?.[0]?.folder_id === null) {
      //     state.files = {
      //       ...(state.files || {}),
      //       data: [...newFiles, ...(state.files?.data || [])],
      //     };
      //     state.largeFile = {
      //       ...(state.largeFile || {}),
      //       data: [...newFiles, ...(state.largeFile?.data || [])],
      //     };
      //   }
      // })

      // Move File To Trash
      // .addCase(TrashFilesFolderAction.fulfilled, (state, action) => {
      //   const trashedFiles = action.payload?.response?.files || [];
      //   const trashedFileIds = trashedFiles.map((f) => f.id);

      //   state.files = {
      //     ...state.files,
      //     data: state.files?.data?.filter(
      //       (file) => !trashedFileIds.includes(file.id),
      //     ),
      //   };
      //   // add this
      //   state.largeFile = {
      //     ...state.largeFile,
      //     data: state.largeFile?.data?.filter(
      //       (file) => !trashedFileIds.includes(file.id),
      //     ),
      //   };
      // })

      // Move File To folder
      .addCase(MoveFileAction.pending, (state) => {
        state.moveFileLoading = true;
      })
      .addCase(MoveFileAction.fulfilled, (state, action) => {
        state.moveFileLoading = false;
        state.files = {
          ...state.files,
          data: state.files?.data?.filter(
            (file) => file.id !== action.payload.fileId,
          ),
        };
      })
      .addCase(MoveFileAction.rejected, (state) => {
        state.moveFileLoading = false;
      })

      .addCase(GetLargeFileAction.pending, (state) => {
        state.largeFilesLoading =
          state.largeFile?.data?.length > 0 ? false : true;
      })

      .addCase(GetLargeFileAction.fulfilled, (state, action) => {
        state.largeFilesLoading = false;

        const newData = action.payload?.response;

        if (newData?.page > 1) {
          state.largeFile = {
            ...newData,
            activeFilter: action.meta.arg,
            data: [...(state.largeFile?.data || []), ...(newData?.data || [])],
          };
        } else {
          state.largeFile = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        }
      })

      .addCase(GetLargeFileAction.rejected, (state) => {
        state.largeFilesLoading = false;
      })

      .addCase(RenameFilesAction.fulfilled, (state, action) => {
        const updatedFile = action.payload.response;

        // update in files
        if (state.files?.data) {
          const index = state.files.data.findIndex(
            (f) => f.id === updatedFile.id,
          );
          if (index !== -1) state.files.data[index] = updatedFile;
        }

        // update in largeFile
        if (state.largeFile?.data) {
          const index = state.largeFile.data.findIndex(
            (f) => f.id === updatedFile.id,
          );
          if (index !== -1) state.largeFile.data[index] = updatedFile;
        }

        [state.largeFile?.data].filter(Boolean).forEach((list) => {
          const file = list?.find((f) => f.id === updatedFile.id);
          if (file) file.file_name = updatedFile.file_name;
        });
      })
      .addCase(CreateShareLinkFileFolderAction.fulfilled, (state, action) => {
        const { shared_link, access_link } = action.payload.response;

        const fileId = action.meta.arg.fileId;

        const generalAccess = {
          visibility: shared_link.visibility,
          role: shared_link.role,
          shared_link: access_link,
          expires_at: shared_link.expires_at,
        };

        if (fileId && state.files?.data) {
          const file = state.files.data.find(
            (item) => item.id === fileId,
          );

          if (file) {
            file.general_access = generalAccess;
          }
        }
        if (fileId && state.largeFile?.data) {
          const file = state.largeFile.data.find(
            (item) => item.id === fileId,
          );

          if (file) {
            file.general_access = generalAccess;
          }
        }

      });
  },
});

export const { renameFileInFiles, uploadFileInFiles,
  trashFiles,
  starUnstarFiles,
  lockUnlockFiles,
  updateFilePermission,
  perminentDeleteFiles,
  trashEmptyFiles,
  restoreFiles
} = filesSlice.actions;
export default filesSlice.reducer;
